import { Controller } from "@hotwired/stimulus";
import {
  addRouteDatapoint,
  getRouteDatapoint,
  getLatestRouteDatapoint,
} from "../../db/routeDatapoints.js";
import {
  recordTrailPosition,
  TRAIL_MAX_INTERVAL_MS,
} from "../../db/backgroundTrail.js";
import { checkOnlineStatus } from "../../onlineStatus.js";
import {
  getNextDatapointUrl,
  resolveBoundSanitationRouteForDocument,
} from "../../db/sanitationRoutes.js";
import { getSanitationForm } from "../../db/sanitationForms.js";
import { serializeForm, distanceMetersBetween } from "../../utils.js";
import Mustache from "mustache";
import { addFileToQueue } from "../../db/fileQueue.js";
import {
  storeFileLocally,
  optimizeImageFile,
  fileToBase64,
} from "../../db/fileStorage.js";
import { DebugLogger } from "../../db/debugLogger.js";
import { dbOpenError } from "../../db/index.js";
import { isQuotaError, reportIfQuotaError } from "../../db/quotaSignal.js";
import {
  navigateToRouteDocument,
  recoverFromBlockedRouteNavigation,
  recoverFromBoundRouteResolutionFailure,
  recoverFromMissingActiveRoute,
  showRouteRecoveryMessage,
} from "../../routeNavigation.js";

export const READY_ACCURACY_M = 15;
export const CAUTION_ACCURACY_M = 30;
export const MINIMUM_ACCURACY = CAUTION_ACCURACY_M;
const MAX_ACCURACY = 300;
const TRIES_LIMIT = 3;
const RETRY_DELAY_MS = 500;
const GEOLOCATION_TIMEOUT_MS = 60000;
export const GPS_STALE_MAX_AGE_MS = 10000;
export const WATCH_FRESH_MAX_AGE_MS = GPS_STALE_MAX_AGE_MS;
const WATCH_FALLBACK_MAX_AGE_MS = 120000;
// Keep in sync with SanitationRoute::GpsQualitySummary's LARGE_JUMP_* constants
// and gps_distance_m haversine — the server re-counts these jumps for the route
// summary, so the thresholds must agree.
const LARGE_JUMP_DISTANCE_M = 30;
const LARGE_JUMP_MAX_ELAPSED_MS = 60000;

export function extractCustomFieldIdForFileInput(form, fileInput) {
  const nameMatch = fileInput.name.match(
    /custom_field_values_attributes\]\[(\d+)\]\[file\]/,
  );
  if (!nameMatch) return null;

  const index = nameMatch[1];
  const customFieldIdInput = form.querySelector(
    `input[name="route_datapoint[custom_field_values_attributes][${index}][custom_field_id]"]`,
  );

  return customFieldIdInput ? parseInt(customFieldIdInput.value) : null;
}

export function findMissingRequiredFile(form) {
  const fileInputs = form.querySelectorAll('input[type="file"][required]');
  for (const input of fileInputs) {
    if (input.closest(".hidden")) continue;
    if (input.files.length === 0) return input;
  }
  return null;
}

export function buildFileQueueEntry({
  sanitationRoute,
  routeDatapointId,
  customFieldId,
  datapointCreatedAt,
  fileStorage,
}) {
  return {
    sanitationRouteId: sanitationRoute.id,
    routeDatapointId,
    customFieldId,
    treeNumber: sanitationRoute.current_tree_number,
    questionType: sanitationRoute.current_question_type,
    datapointCreatedAt,
    fileLocalId: fileStorage.fileId,
    fileName: fileStorage.name,
    fileSize: fileStorage.size,
    fileType: fileStorage.type,
    localPath: fileStorage.localPath,
  };
}

function numericAccuracy(position) {
  const accuracy = position?.coords?.accuracy;
  return Number.isFinite(accuracy) ? accuracy : null;
}

export function gpsAgeMsFor(position, now = Date.now()) {
  if (!position?.timestamp) return null;
  return Math.max(0, now - position.timestamp);
}

export function classifyGpsQuality(
  position,
  { now = Date.now(), source = null } = {},
) {
  if (!position?.coords) return "missing";
  if (source === "cached_fallback") return "stale";

  const ageMs = gpsAgeMsFor(position, now);
  if (ageMs != null && ageMs > GPS_STALE_MAX_AGE_MS) return "stale";

  const accuracy = numericAccuracy(position);
  if (accuracy == null) return "weak";
  if (accuracy <= READY_ACCURACY_M) return "ready";
  if (accuracy <= CAUTION_ACCURACY_M) return "caution";
  return "weak";
}

export function locationStatusForGpsMetadata(gpsMetadata) {
  if (gpsMetadata?.gpsQuality === "missing") return "missing";
  if (gpsMetadata?.gpsQuality === "stale") return "stale";
  return "ok";
}

export function gpsMetadataForPosition(
  position,
  { source = "current", now = Date.now(), override = false } = {},
) {
  const accuracy = numericAccuracy(position);
  return {
    gpsAccuracyM: accuracy,
    gpsRecordedAt: position?.timestamp
      ? new Date(position.timestamp).toISOString()
      : null,
    gpsAgeMs: gpsAgeMsFor(position, now),
    gpsQuality: classifyGpsQuality(position, { now, source }),
    gpsSource: source,
    gpsOverride: override,
  };
}

export function gpsRequiresOverride(gpsMetadata) {
  return ["weak", "stale", "missing"].includes(gpsMetadata?.gpsQuality);
}

const GPS_DOCK_VARIANTS = {
  ready: { color: "text-primary", icon: "i-ph-crosshair" },
  caution: { color: "text-warning", icon: "i-ph-crosshair-simple" },
  weak: { color: "text-error", icon: "i-ph-crosshair-simple" },
  stale: { color: "text-warning", icon: "i-ph-clock-countdown" },
  missing: {
    color: "text-error",
    icon: "i-ph-map-pin-slash",
    forceGpsLabel: true,
  },
};

export function gpsDockStateFor(gpsMetadata) {
  const quality = gpsMetadata?.gpsQuality || "acquiring";
  const accuracy = Number.isFinite(gpsMetadata?.gpsAccuracyM)
    ? Math.round(gpsMetadata.gpsAccuracyM)
    : null;
  const variant = GPS_DOCK_VARIANTS[quality];

  // No usable fix yet (or an unknown quality): show the acquiring spinner.
  if (!variant) {
    return {
      text: "GPS",
      buttonClass: "touch-manipulation text-base-content/60",
      iconClass: "loading loading-spinner w-8 h-8 text-primary",
      textClass: "text-xs font-bold leading-none text-base-content/60",
    };
  }

  return {
    text: variant.forceGpsLabel || accuracy == null ? "GPS" : `±${accuracy}m`,
    buttonClass: `touch-manipulation ${variant.color}`,
    iconClass: `w-9 h-9 ${variant.icon} ${variant.color}`,
    textClass: `text-xs font-bold leading-none ${variant.color}`,
  };
}

export class RouteContextMismatchError extends Error {
  constructor({ reason, recoveryUrl, message } = {}) {
    super(
      message ||
        "La página no coincide con el recorrido local. No se guardó nada.",
    );
    this.name = "RouteContextMismatchError";
    this.reason = reason;
    this.recoveryUrl = recoveryUrl;
  }
}

export async function persistRouteDatapointSubmission({
  form,
  pageFarmId = null,
  pageSanitationFormId = null,
  pageQuestionId = null,
  pageQuestionType = null,
  position,
  locationStatus = "ok",
  gpsMetadata = null,
  latInput,
  lonInput,
  locationStatusInput = null,
  gpsInputs = {},
  processFileInputs,
  addRouteDatapointFn = addRouteDatapoint,
  getNextDatapointUrlFn = getNextDatapointUrl,
  serializeFormFn = serializeForm,
  resolveBoundSanitationRouteForDocumentFn = resolveBoundSanitationRouteForDocument,
}) {
  latInput.value = position ? position.coords.latitude : "";
  lonInput.value = position ? position.coords.longitude : "";
  if (locationStatusInput) locationStatusInput.value = locationStatus;
  if (gpsInputs.gpsAccuracyInput)
    gpsInputs.gpsAccuracyInput.value = gpsMetadata?.gpsAccuracyM ?? "";
  if (gpsInputs.gpsRecordedAtInput)
    gpsInputs.gpsRecordedAtInput.value = gpsMetadata?.gpsRecordedAt ?? "";
  if (gpsInputs.gpsAgeInput)
    gpsInputs.gpsAgeInput.value = gpsMetadata?.gpsAgeMs ?? "";
  if (gpsInputs.gpsQualityInput)
    gpsInputs.gpsQualityInput.value = gpsMetadata?.gpsQuality ?? "";
  if (gpsInputs.gpsSourceInput)
    gpsInputs.gpsSourceInput.value = gpsMetadata?.gpsSource ?? "";
  if (gpsInputs.gpsOverrideInput)
    gpsInputs.gpsOverrideInput.value = gpsMetadata?.gpsOverride
      ? "true"
      : "false";

  const serialized = serializeFormFn(form);
  const hiddenQuestionId =
    serialized?.route_datapoint?.sanitation_form_question_id;
  const hiddenDatapointType = serialized?.route_datapoint?.datapoint_type;
  const hiddenFarmId = serialized?.route_datapoint?.farm_id;

  const effectiveQuestionId =
    pageQuestionId != null ? pageQuestionId : hiddenQuestionId;
  const effectiveQuestionType =
    pageQuestionType != null ? pageQuestionType : hiddenDatapointType;
  const effectiveFarmId = pageFarmId != null ? pageFarmId : hiddenFarmId;

  if (
    pageQuestionId != null &&
    hiddenQuestionId != null &&
    Number(pageQuestionId) !== Number(hiddenQuestionId)
  ) {
    throw new RouteContextMismatchError({
      reason: "hidden_question_mismatch",
      recoveryUrl: null,
    });
  }

  if (
    pageQuestionType != null &&
    hiddenDatapointType != null &&
    String(pageQuestionType) !== String(hiddenDatapointType)
  ) {
    throw new RouteContextMismatchError({
      reason: "hidden_type_mismatch",
      recoveryUrl: null,
    });
  }

  const resolution = await resolveBoundSanitationRouteForDocumentFn({
    pageFarmId: effectiveFarmId,
    pageSanitationFormId,
    pageQuestionId: effectiveQuestionId,
    pageQuestionType: effectiveQuestionType,
  });

  if (!resolution.ok) {
    throw new RouteContextMismatchError({
      reason: resolution.reason,
      recoveryUrl: resolution.recoveryUrl,
    });
  }

  const sanitationRoute = resolution.route;

  const routeDatapointId = await addRouteDatapointFn({
    data: serialized,
    sanitationRouteId: sanitationRoute.id,
    treeNumber: sanitationRoute.current_tree_number,
    questionType: sanitationRoute.current_question_type,
  });

  await processFileInputs(form, sanitationRoute, routeDatapointId);

  return await getNextDatapointUrlFn(sanitationRoute.id, {
    intent: "next_step",
  });
}

export function accuracyThresholdFor(triesLeft) {
  if (triesLeft >= TRIES_LIMIT) return READY_ACCURACY_M;
  if (triesLeft === TRIES_LIMIT - 1) return CAUTION_ACCURACY_M;
  return MAX_ACCURACY;
}

export function isUsableWatchPosition(
  position,
  { maxAgeMs, maxAccuracy, now = Date.now() },
) {
  if (!position?.coords) return false;
  return (
    now - position.timestamp <= maxAgeMs &&
    position.coords.accuracy <= maxAccuracy
  );
}

export function resolveNoGpsFallback({
  lastWatchedPosition,
  message,
  now = Date.now(),
  confirmFn = window.confirm,
}) {
  // Fallback text covers offline-cached documents rendered before the
  // translated message value existed.
  const accepted = confirmFn(
    message ||
      "No se pudo obtener la ubicación GPS. ¿Deseas guardar este punto sin ubicación y continuar?",
  );
  if (!accepted) return null;
  if (lastWatchedPosition?.coords) {
    const gpsMetadata = gpsMetadataForPosition(lastWatchedPosition, {
      source: "cached_fallback",
      now,
      override: true,
    });
    return {
      position: lastWatchedPosition,
      locationStatus: "stale",
      gpsMetadata,
    };
  }
  const gpsMetadata = gpsMetadataForPosition(null, {
    source: "manual_missing",
    override: true,
  });
  return { position: null, locationStatus: "missing", gpsMetadata };
}

export function requestAccuratePosition({
  triesLeft,
  successCallback,
  errorCallback,
  geolocation = navigator.geolocation,
  hostname = window.location.hostname,
  geolocationOptions,
  retryDelayMs = RETRY_DELAY_MS,
  getCachedFallbackPosition = () => null,
  onProgress = () => {},
}) {
  const retryArgs = {
    successCallback,
    errorCallback,
    geolocation,
    hostname,
    geolocationOptions,
    retryDelayMs,
    getCachedFallbackPosition,
    onProgress,
  };

  const resolveWithFallbackOr = (terminalError) => {
    const fallback = getCachedFallbackPosition();
    if (fallback) {
      DebugLogger.warn("Using cached watch position as geolocation fallback");
      const gpsMetadata = gpsMetadataForPosition(fallback, {
        source: "cached_fallback",
      });
      successCallback(fallback, {
        source: "cached_fallback",
        locationStatus: locationStatusForGpsMetadata(gpsMetadata),
        gpsMetadata,
      });
      return;
    }
    errorCallback(terminalError);
  };

  if (triesLeft <= 0) {
    resolveWithFallbackOr(
      new Error(
        "No es posible obtener una ubicación precisa. Por favor, inténtelo de nuevo. Asegúrate que tu dispositivo tenga activado la opción de alta precision para el GPS",
      ),
    );
    return;
  }

  const options = {
    ...geolocationOptions,
    maximumAge: triesLeft < TRIES_LIMIT ? 0 : geolocationOptions.maximumAge,
  };

  const accuracyThreshold = accuracyThresholdFor(triesLeft);

  onProgress("attempt", {
    attempt: TRIES_LIMIT - triesLeft + 1,
    total: TRIES_LIMIT,
    accuracyThreshold,
  });

  geolocation.getCurrentPosition(
    (position) => {
      const gpsMetadata = gpsMetadataForPosition(position, {
        source: "current",
      });
      if (position.coords.accuracy <= accuracyThreshold) {
        successCallback(position, {
          source: "current",
          locationStatus: locationStatusForGpsMetadata(gpsMetadata),
          gpsMetadata,
        });
      } else {
        onProgress("accuracy", {
          accuracy: position.coords.accuracy,
          quality: gpsMetadata.gpsQuality,
          gpsMetadata,
        });
        console.log(
          `Low accuracy: ${
            position.coords.accuracy
          }m (threshold: ${accuracyThreshold}m), Tries left: ${triesLeft - 1}`,
        );
        setTimeout(() => {
          requestAccuratePosition({ ...retryArgs, triesLeft: triesLeft - 1 });
        }, retryDelayMs);
      }
    },
    (error) => {
      // Mock location on localhost for development
      if (
        (hostname === "localhost" || hostname === "127.0.0.1") &&
        error.code === error.POSITION_UNAVAILABLE
      ) {
        console.log(
          "Mocking location for localhost development (requestAccuratePosition)",
        );
        const mockPosition = {
          coords: {
            latitude: 4.881852,
            longitude: -74.032407,
            accuracy: 10,
          },
          timestamp: Date.now(),
        };
        const gpsMetadata = gpsMetadataForPosition(mockPosition, {
          source: "mock",
        });
        successCallback(mockPosition, {
          source: "mock",
          locationStatus: locationStatusForGpsMetadata(gpsMetadata),
          gpsMetadata,
        });
        return;
      }
      if (error.code === error.TIMEOUT && triesLeft > 1) {
        console.log(
          `Geolocation timeout, retrying. Tries left: ${triesLeft - 1}`,
        );
        setTimeout(() => {
          requestAccuratePosition({ ...retryArgs, triesLeft: triesLeft - 1 });
        }, retryDelayMs);
        return;
      }
      resolveWithFallbackOr(error);
    },
    options,
  );
}

// Connects to data-controller="route-datapoints--form"
export default class extends Controller {
  static targets = [
    "container",
    "form",
    "lat",
    "lon",
    "locationStatus",
    "gpsAccuracy",
    "gpsRecordedAt",
    "gpsAge",
    "gpsQuality",
    "gpsSource",
    "gpsOverride",
    "pauseButton",
    "submitButton",
    "loadingSpinner",
    "nextIcon",
    "recoveryMessage",
    "recoveryMessageText",
    "recoveryLink",
    "gpsStatus",
    "gpsStatusText",
    "gpsPrecisionIndicator",
    "gpsPrecisionIcon",
    "gpsPrecisionText",
  ];

  static values = {
    routeLauncherPath: String,
    savedOfflineBlockedMessage: String,
    storageFullMessage: String,
    noGpsConfirmMessage: String,
    lowPrecisionConfirmMessage: String,
    gpsJumpConfirmMessage: String,
    gpsSearchingMessage: String,
    gpsAttemptMessage: String,
    gpsHoldStillMessage: String,
    farmId: Number,
    sanitationFormId: Number,
    sanitationFormQuestionId: Number,
    datapointType: String,
    routeContextMismatchMessage: String,
    nothingSavedMessage: String,
  };

  static geolocationOptions = {
    enableHighAccuracy: true,
    maximumAge: 40000,
    timeout: GEOLOCATION_TIMEOUT_MS,
  };

  async connect() {
    try {
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;

      const resolution = await resolveBoundSanitationRouteForDocument({
        pageFarmId: this.hasFarmIdValue ? this.farmIdValue : null,
        pageSanitationFormId: this.hasSanitationFormIdValue
          ? this.sanitationFormIdValue
          : null,
        pageQuestionId: this.hasSanitationFormQuestionIdValue
          ? this.sanitationFormQuestionIdValue
          : null,
        pageQuestionType: this.hasDatapointTypeValue
          ? this.datapointTypeValue
          : null,
      });

      if (!resolution.ok) {
        await this.handleConnectResolutionFailure(resolution);
        return;
      }

      const sanitationRoute = resolution.route;
      const sanitationForm = await getSanitationForm(
        sanitationRoute.sanitation_form_id,
      );

      if (!sanitationForm) {
        console.error("Associated form not found locally for bound route.");
        await recoverFromMissingActiveRoute(
          this,
          this.routeContextMismatchMessageValue ||
            "Error: No se encontró el formulario asociado. Por favor, regrese al lanzador de rutas.",
          { disableControls: () => this.disableControlsForRecovery() },
        );
        return;
      }

      const containerTemplate = this.containerTarget.innerHTML;
      const rendered = Mustache.render(containerTemplate, {
        current_tree_number: sanitationRoute?.current_tree_number ?? 1,
        total_trees_in_route: sanitationRoute?.total_trees_in_route ?? 2,
        current_section_number: sanitationRoute?.current_section_number ?? 1,
        current_sample_number: sanitationRoute?.current_sample_number ?? 1,
        samples_per_tree_section:
          sanitationRoute?.samples_per_tree_section ?? 1,
        number_of_tree_sections: sanitationRoute?.number_of_tree_sections ?? 1,
      });

      this.pageLoadTimestamp = Date.now();
      this.containerTarget.innerHTML = rendered;
      this.sanitationRoute = sanitationRoute;
      this.updateGpsPrecisionDock(null);

      if (!navigator.geolocation) {
        alert(
          "Este dispositivo no soporta la geolocalización. No es posible continuar el recorrido",
        );
        this.disableControlsForRecovery();
        return;
      }

      this.startPositionWatch();

      if (sanitationForm.open_ended === true) {
        this.startBackgroundTracking();
      }

      if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false;
    } catch (error) {
      console.error("Error initializing form:", error);
      DebugLogger.error("Route datapoint form failed to initialize", {
        error: error?.message,
        dbOpenError: dbOpenError?.message,
      });
      alert("Error al cargar el formulario. Por favor, recargue la página.");
      this.disableControlsForRecovery();
    }
  }

  disableControlsForRecovery() {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
    if (this.hasPauseButtonTarget)
      this.pauseButtonTarget.style.pointerEvents = "none";
  }

  async handleConnectResolutionFailure(resolution) {
    DebugLogger.warn("Route datapoint page failed route binding validation", {
      reason: resolution.reason,
      recoveryUrl: resolution.recoveryUrl,
    });

    return recoverFromBoundRouteResolutionFailure(this, resolution, {
      message:
        this.routeContextMismatchMessageValue ||
        "No se pudo vincular esta pantalla al recorrido local. Regresa al lanzador de rutas.",
      disableControls: () => this.disableControlsForRecovery(),
    });
  }

  showSubmitMismatchRecovery(recoveryUrl) {
    const message =
      this.nothingSavedMessageValue ||
      "Nada se guardó. Esta pantalla no coincide con el recorrido local.";
    showRouteRecoveryMessage(this, message);

    if (this.hasRecoveryLinkTarget) {
      if (recoveryUrl) {
        this.recoveryLinkTarget.href = recoveryUrl;
        this.recoveryLinkTarget.classList.remove("hidden");
      } else {
        this.recoveryLinkTarget.classList.add("hidden");
      }
    }
  }

  disconnect() {
    this.endGpsFeedback();
    if (this.geoInterval) {
      clearInterval(this.geoInterval);
    }
    if (this.geoWatchId != null) {
      navigator.geolocation.clearWatch(this.geoWatchId);
      this.geoWatchId = null;
    }
  }

  startPositionWatch() {
    this.lastWatchedPosition = null;
    this.geoWatchId = navigator.geolocation.watchPosition(
      (position) => {
        this.lastWatchedPosition = position;
        this.updateGpsPrecisionDock(
          gpsMetadataForPosition(position, { source: "watch" }),
        );
        this.recordTrailFromWatch(position);
      },
      (error) => {
        console.warn("Position watch error:", error);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 120000 },
    );
  }

  startBackgroundTracking() {
    if (!this.sanitationRoute) {
      console.warn("Cannot start background tracking without an active route.");
      return;
    }
    // The trail is fed from the already-running position watch (no extra GPS
    // requests). The interval only covers long stationary periods where the
    // watch stops emitting; it reuses the last watch fix through the same gate.
    this.trailEnabled = true;
    this.geoInterval = setInterval(() => {
      if (this.lastWatchedPosition) {
        this.recordTrailFromWatch(this.lastWatchedPosition);
      }
    }, TRAIL_MAX_INTERVAL_MS);
  }

  recordTrailFromWatch(position) {
    if (!this.trailEnabled) return Promise.resolve(false);
    return recordTrailPosition(this.sanitationRoute, position, () =>
      gpsMetadataForPosition(position, { source: "watch" }),
    );
  }

  setLoadingState(isLoading) {
    if (!isLoading) this.endGpsFeedback();
    if (
      !this.hasSubmitButtonTarget ||
      !this.hasNextIconTarget ||
      !this.hasLoadingSpinnerTarget
    )
      return;
    this.submitButtonTarget.disabled = isLoading;
    this.nextIconTarget.classList.toggle("hidden", isLoading);
    this.loadingSpinnerTarget.classList.toggle("hidden", !isLoading);
  }

  updateGpsPrecisionDock(gpsMetadata) {
    if (
      !this.hasGpsPrecisionIndicatorTarget ||
      !this.hasGpsPrecisionIconTarget ||
      !this.hasGpsPrecisionTextTarget
    )
      return;

    const state = gpsDockStateFor(gpsMetadata);
    this.gpsPrecisionIndicatorTarget.className = state.buttonClass;
    this.gpsPrecisionIconTarget.className = state.iconClass;
    this.gpsPrecisionTextTarget.className = state.textClass;
    this.gpsPrecisionTextTarget.textContent = state.text;
  }

  showGpsGuidance(message) {
    if (!this.hasGpsStatusTarget || !this.hasGpsStatusTextTarget) return;
    this.gpsStatusTarget.classList.remove("hidden");
    this.gpsStatusTextTarget.textContent = message;
  }

  beginGpsFeedback() {
    // Offline-cached documents rendered before this element existed won't
    // have the target — degrade to the old spinner-only behavior.
    if (!this.hasGpsStatusTarget) return;
    this.gpsAttemptInfo = { attempt: 1, total: TRIES_LIMIT };
    this.gpsFeedbackStartedAt = Date.now();
    this.gpsStatusTarget.classList.remove("hidden");
    this.renderGpsStatus();
    this.gpsStatusInterval = setInterval(() => this.renderGpsStatus(), 1000);
  }

  updateGpsProgress(stage, info) {
    if (stage === "attempt") {
      this.gpsAttemptInfo = info;
      this.renderGpsStatus();
      return;
    }
    if (stage === "accuracy") {
      this.updateGpsPrecisionDock(info.gpsMetadata);
      if (["weak", "stale"].includes(info.gpsMetadata?.gpsQuality)) {
        this.showGpsGuidance(
          this.gpsHoldStillMessageValue || "Quédate quieto unos segundos",
        );
      }
    }
  }

  renderGpsStatus() {
    if (!this.hasGpsStatusTextTarget) return;
    const searching = this.gpsSearchingMessageValue || "Buscando señal GPS…";
    const attemptTemplate =
      this.gpsAttemptMessageValue || "intento {current} de {total}";
    const attempt = attemptTemplate
      .replace("{current}", this.gpsAttemptInfo.attempt)
      .replace("{total}", this.gpsAttemptInfo.total);
    const elapsed = Math.round((Date.now() - this.gpsFeedbackStartedAt) / 1000);
    this.gpsStatusTextTarget.textContent = `${searching} ${attempt} · ${elapsed} s`;
  }

  endGpsFeedback() {
    if (this.gpsStatusInterval) {
      clearInterval(this.gpsStatusInterval);
      this.gpsStatusInterval = null;
    }
    if (this.hasGpsStatusTarget) {
      this.gpsStatusTarget.classList.add("hidden");
    }
  }

  async pauseRoute(e) {
    e.preventDefault();

    if (!this.hasPauseButtonTarget) return;

    const routeLauncherPath = this.pauseButtonTarget.getAttribute("href");
    const confirmText = this.pauseButtonTarget.getAttribute("data-confirm");

    if (!confirm(confirmText)) return;

    // Pausing just returns to the launcher. The route keeps its in_progress
    // status and last_active_at, so re-entering it (card, back, reload) still
    // resolves it as the active route.
    window.location.href = routeLauncherPath;
  }

  async processFileInputs(form, sanitationRoute, routeDatapointId) {
    const fileInputs = form.querySelectorAll('input[type="file"]');

    // Get the datapoint's created_at for unique file matching during upload.
    // Multiple tree_samples per tree share the same tree_number + question_type,
    // so created_at is needed to match each file to its exact datapoint.
    const datapoint = await getRouteDatapoint(routeDatapointId);
    const datapointCreatedAt = datapoint?.created_at || null;

    for (const input of fileInputs) {
      if (input.files.length > 0) {
        // Extract custom_field_id from input name
        const customFieldId = this.extractCustomFieldId(input);
        if (!customFieldId) {
          console.warn(
            "Could not extract custom_field_id from file input:",
            input.name,
          );
          continue;
        }

        for (const file of input.files) {
          try {
            // Optimize image files
            const optimizedFile = await optimizeImageFile(file);

            // Store file locally
            const fileStorage = await storeFileLocally(optimizedFile);

            // Add to file queue with route datapoint ID
            await addFileToQueue(
              buildFileQueueEntry({
                sanitationRoute,
                routeDatapointId,
                customFieldId,
                datapointCreatedAt,
                fileStorage,
              }),
            );
          } catch (error) {
            console.error("Error processing file:", file.name, error);
            alert(
              `Error al procesar el archivo ${file.name}: ${error.message}`,
            );
            throw error;
          }
        }
      }
    }
  }

  extractCustomFieldId(fileInput) {
    return extractCustomFieldIdForFileInput(this.formTarget, fileInput);
  }

  gpsInputTargets() {
    const targetNames = [
      "gpsAccuracy",
      "gpsRecordedAt",
      "gpsAge",
      "gpsQuality",
      "gpsSource",
      "gpsOverride",
    ];
    return Object.fromEntries(
      targetNames.map((name) => {
        const capitalized = name[0].toUpperCase() + name.slice(1);
        const target = this[`has${capitalized}Target`]
          ? this[`${name}Target`]
          : null;
        return [`${name}Input`, target];
      }),
    );
  }

  confirmGpsOverrideIfNeeded(gpsResult) {
    if (!gpsRequiresOverride(gpsResult?.gpsMetadata)) return gpsResult;
    if (gpsResult.gpsMetadata.gpsOverride) return gpsResult;

    const message =
      gpsResult.gpsMetadata.gpsQuality === "missing"
        ? this.noGpsConfirmMessageValue
        : this.lowPrecisionConfirmMessageValue ||
          "La precisión GPS no es ideal. ¿Deseas guardar este punto de todas formas?";

    if (!confirm(message)) return null;

    const gpsMetadata = {
      ...gpsResult.gpsMetadata,
      gpsOverride: true,
    };

    this.updateGpsPrecisionDock(gpsMetadata);
    return {
      ...gpsResult,
      gpsMetadata,
    };
  }

  async confirmGpsJumpIfNeeded(position, sanitationRoute) {
    if (!position?.coords) return true;

    const latestDatapoint = await getLatestRouteDatapoint(sanitationRoute.id);
    const previousPoint = this.coordinatesFromDatapoint(latestDatapoint);
    if (!previousPoint) return true;

    const currentPoint = {
      lat: position.coords.latitude,
      lon: position.coords.longitude,
    };
    const distanceM = distanceMetersBetween(previousPoint, currentPoint);
    const elapsedMs = latestDatapoint?.created_at
      ? Date.now() - Date.parse(latestDatapoint.created_at)
      : null;

    if (
      distanceM == null ||
      elapsedMs == null ||
      distanceM <= LARGE_JUMP_DISTANCE_M ||
      elapsedMs >= LARGE_JUMP_MAX_ELAPSED_MS
    ) {
      return true;
    }

    this.showGpsGuidance(
      this.gpsHoldStillMessageValue || "Quédate quieto unos segundos",
    );
    return confirm(
      this.gpsJumpConfirmMessageValue ||
        "El GPS saltó más de 30 m en menos de un minuto. ¿Deseas guardar este punto de todas formas?",
    );
  }

  coordinatesFromDatapoint(routeDatapoint) {
    const data = routeDatapoint?.data?.route_datapoint || {};
    const lat = data.lat;
    const lon = data.lon;
    if (lat === "" || lon === "" || lat == null || lon == null) return null;
    return { lat, lon };
  }

  async submit(e) {
    e.preventDefault();

    if (
      !this.hasSubmitButtonTarget ||
      !this.hasFormTarget ||
      !this.hasLatTarget ||
      !this.hasLonTarget
    )
      return;

    // Trigger HTML5 form validation
    if (!this.formTarget.checkValidity()) {
      this.formTarget.reportValidity();
      return;
    }

    // Explicit check for required file inputs (belt-and-suspenders for mobile browsers
    // where checkValidity() doesn't reliably validate file inputs)
    const missingFileInput = findMissingRequiredFile(this.formTarget);
    if (missingFileInput) {
      missingFileInput.scrollIntoView({ behavior: "smooth", block: "center" });
      const fieldName =
        missingFileInput
          .closest("fieldset")
          ?.querySelector("label")
          ?.textContent?.trim() || "Foto requerida";
      alert(fieldName);
      return;
    }

    if (this.submitButtonTarget.disabled) return;

    this.setLoadingState(true);

    const currentTime = Date.now();
    const elapsedTime = currentTime - this.pageLoadTimestamp;

    if (elapsedTime <= 1000) {
      this.setLoadingState(false);
      alert(
        "Doble click detectado, asegúrate que estés enviando la información correcta",
      );
      return;
    }

    try {
      let gpsResult;
      try {
        gpsResult = await this.getCurrentPositionWithStatus();
      } catch (geoError) {
        const fallback = resolveNoGpsFallback({
          lastWatchedPosition: this.lastWatchedPosition,
          message: this.noGpsConfirmMessageValue,
        });
        if (!fallback) throw geoError;
        DebugLogger.warn("Saving route datapoint without reliable GPS", {
          locationStatus: fallback.locationStatus,
        });
        gpsResult = fallback;
      }

      gpsResult = this.confirmGpsOverrideIfNeeded(gpsResult);
      if (!gpsResult) {
        this.setLoadingState(false);
        return;
      }

      const boundRoute = this.sanitationRoute;
      if (!boundRoute) {
        this.setLoadingState(false);
        this.showSubmitMismatchRecovery(null);
        return;
      }

      const shouldContinueAfterJump = await this.confirmGpsJumpIfNeeded(
        gpsResult.position,
        boundRoute,
      );
      if (!shouldContinueAfterJump) {
        this.setLoadingState(false);
        return;
      }

      const nextStepUrl = await persistRouteDatapointSubmission({
        form: this.formTarget,
        pageFarmId: this.hasFarmIdValue ? this.farmIdValue : null,
        pageSanitationFormId: this.hasSanitationFormIdValue
          ? this.sanitationFormIdValue
          : null,
        pageQuestionId: this.hasSanitationFormQuestionIdValue
          ? this.sanitationFormQuestionIdValue
          : null,
        pageQuestionType: this.hasDatapointTypeValue
          ? this.datapointTypeValue
          : null,
        position: gpsResult.position,
        locationStatus: gpsResult.locationStatus,
        gpsMetadata: gpsResult.gpsMetadata,
        latInput: this.latTarget,
        lonInput: this.lonTarget,
        locationStatusInput: this.hasLocationStatusTarget
          ? this.locationStatusTarget
          : null,
        gpsInputs: this.gpsInputTargets(),
        processFileInputs: (...args) => this.processFileInputs(...args),
      });
      await this.navigateAfterLocalSave(nextStepUrl);
    } catch (error) {
      if (error instanceof RouteContextMismatchError) {
        this.setLoadingState(false);
        this.showSubmitMismatchRecovery(error.recoveryUrl);
        DebugLogger.warn("Route datapoint submit blocked by route binding", {
          reason: error.reason,
          recoveryUrl: error.recoveryUrl,
        });
        return;
      }
      const message =
        error.message ||
        "No se pudo guardar la información. Por favor, inténtelo de nuevo.";
      this.handleError(error.code ? error : new Error(message));
    }
  }

  getCurrentPositionWithStatus() {
    const cached = this.lastWatchedPosition;
    if (
      isUsableWatchPosition(cached, {
        maxAgeMs: WATCH_FRESH_MAX_AGE_MS,
        maxAccuracy: CAUTION_ACCURACY_M,
      })
    ) {
      const gpsMetadata = gpsMetadataForPosition(cached, { source: "watch" });
      this.updateGpsPrecisionDock(gpsMetadata);
      return Promise.resolve({
        position: cached,
        locationStatus: locationStatusForGpsMetadata(gpsMetadata),
        gpsMetadata,
      });
    }
    return new Promise((resolve, reject) => {
      this.beginGpsFeedback();
      requestAccuratePosition({
        triesLeft: TRIES_LIMIT,
        successCallback: (position, metadata = {}) => {
          this.endGpsFeedback();
          const gpsMetadata =
            metadata.gpsMetadata ||
            gpsMetadataForPosition(position, {
              source: metadata.source || "current",
            });
          this.updateGpsPrecisionDock(gpsMetadata);
          resolve({
            position,
            locationStatus:
              metadata.locationStatus ||
              locationStatusForGpsMetadata(gpsMetadata),
            gpsMetadata,
          });
        },
        errorCallback: (error) => {
          this.endGpsFeedback();
          reject(error);
        },
        onProgress: (stage, info) => this.updateGpsProgress(stage, info),
        geolocationOptions: this.constructor.geolocationOptions,
        getCachedFallbackPosition: () =>
          isUsableWatchPosition(this.lastWatchedPosition, {
            maxAgeMs: WATCH_FALLBACK_MAX_AGE_MS,
            maxAccuracy: MAX_ACCURACY,
          })
            ? this.lastWatchedPosition
            : null,
      });
    });
  }

  getCurrentPosition() {
    return this.getCurrentPositionWithStatus().then(({ position }) => position);
  }

  handleError(error) {
    console.error("Form Error:", error);
    this.setLoadingState(false);

    reportIfQuotaError(error);
    if (isQuotaError(error)) {
      // A raw QuotaExceededError is meaningless to a monitor — say what
      // happened, what to do, and that captured data is safe.
      alert(
        this.storageFullMessageValue ||
          "El dispositivo no tiene espacio, así que no se pudo guardar esta muestra. Sube tus recorridos pendientes o libera espacio en el dispositivo, y vuelve a intentarlo. Lo que ya capturaste sigue guardado.",
      );
      return;
    }

    let message = error.message || "Ocurrió un error desconocido.";

    if (error.code) {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          message =
            "Permiso denegado. Por favor, habilite los servicios de ubicación.";
          break;
        case error.POSITION_UNAVAILABLE:
          // Mock location on localhost to allow development flow to continue
          if (
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1"
          ) {
            console.log("Mocking location for localhost development");
            this.handleMockedPosition();
            return;
          }
          message =
            "Posición no disponible. Verifique la señal GPS e inténtelo de nuevo.";
          break;
        case error.TIMEOUT:
          message =
            "La solicitud de ubicación ha caducado. Por favor, inténtelo de nuevo.";
          break;
        default:
          message = `Error de geolocalización desconocido (Code: ${error.code}). Por favor, inténtelo de nuevo.`;
      }
    }

    alert(message);
  }

  handleMockedPosition() {
    // Create a mock position object with coordinates (Mexico City coordinates as example)
    const mockPosition = {
      coords: {
        latitude: 4.881852,
        longitude: -74.032407,
        accuracy: 10,
      },
      timestamp: Date.now(),
    };

    // Continue with the submit flow using the mocked position
    if (this.hasLatTarget && this.hasLonTarget) {
      this.latTarget.value = mockPosition.coords.latitude;
      this.lonTarget.value = mockPosition.coords.longitude;

      // Continue with the rest of the submit logic
      this.continueSubmitWithPosition(mockPosition);
    }
  }

  async continueSubmitWithPosition(position) {
    try {
      const gpsMetadata = gpsMetadataForPosition(position, {
        source: "current",
      });
      const nextStepUrl = await persistRouteDatapointSubmission({
        form: this.formTarget,
        pageFarmId: this.hasFarmIdValue ? this.farmIdValue : null,
        pageSanitationFormId: this.hasSanitationFormIdValue
          ? this.sanitationFormIdValue
          : null,
        pageQuestionId: this.hasSanitationFormQuestionIdValue
          ? this.sanitationFormQuestionIdValue
          : null,
        pageQuestionType: this.hasDatapointTypeValue
          ? this.datapointTypeValue
          : null,
        position,
        locationStatus: locationStatusForGpsMetadata(gpsMetadata),
        gpsMetadata,
        latInput: this.latTarget,
        lonInput: this.lonTarget,
        locationStatusInput: this.hasLocationStatusTarget
          ? this.locationStatusTarget
          : null,
        gpsInputs: this.gpsInputTargets(),
        processFileInputs: (...args) => this.processFileInputs(...args),
      });
      await this.navigateAfterLocalSave(nextStepUrl);
    } catch (error) {
      if (error instanceof RouteContextMismatchError) {
        this.setLoadingState(false);
        this.showSubmitMismatchRecovery(error.recoveryUrl);
        return;
      }
      const message =
        error.message ||
        "No se pudo guardar la información. Por favor, inténtelo de nuevo.";
      this.handleError(new Error(message));
    }
  }

  async navigateAfterLocalSave(nextStepUrl) {
    const result = await navigateToRouteDocument(nextStepUrl);
    if (result.allowed) return result;

    await recoverFromBlockedRouteNavigation(this, result, {
      warnMessage: "Route datapoint saved but next screen is missing offline",
      disableControls: () => {
        this.setLoadingState(false);
        if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
        if (this.hasPauseButtonTarget)
          this.pauseButtonTarget.style.pointerEvents = "none";
      },
    });
    return result;
  }
}
